// CategoriesView.js
// This component displays a dashboard-style view of all learning categories.
// Users can add, edit, or delete categories — each category links to its own questions page.
// Includes RTL layout support, dynamic dialogs, and a responsive grid UI using MUI components.
import React, { Component } from "react";
import { Link } from "react-router-dom";

import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Fab, Grid, ListItemButton,
    ListItemIcon, Paper, TextField, Typography, Menu, MenuItem, Chip, IconButton
} from "@mui/material";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

// Firebase operations for managing category data
import { addCategory, deleteCategory, getCategories, getQuestionCountByCategory, updateCategoryName } from "../../firebase-handler";

// RTL (Right-to-Left) setup for Hebrew/Arabic UI support
import { createTheme , ThemeProvider } from '@mui/material/styles';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from 'stylis';

// Create Emotion cache for RTL styles
const cacheRtl = createCache( {
    key : 'muirtl' ,
    stylisPlugins : [prefixer , rtlPlugin] ,
} );

// Theme object for MUI that enables RTL
const theme = createTheme( {
    direction : 'rtl' ,
} );

// Class-based React component for listing and managing learning categories
class CategoriesView extends Component {
    constructor( props ) {
        super( props );
        this.state = {
            categories : [] ,                   // Holds list of category objects
            dialogOpen : false ,                // Controls visibility of "Add Category" dialog
            newCategoryName : "" ,              // Controlled input for new category name
            newMaterial : "" ,                  // Controlled input for new study material
            editDialogOpen : false ,            // Controls visibility of "Edit Category" dialog
            editCategoryName : "" ,             // Input value when renaming a category
            editCategoryId : null ,             // ID of category being edited
            menuAnchorEl: null,                 // Reference to the category menu trigger (3-dots)
            selectedCategory: null,             // Currently selected category for edit/delete
        };
    }

    // Open "Add Category" dialog
    handleOpenDialog = () => {
        this.setState( { dialogOpen : true } );
    };

    // Close "Add Category" dialog and reset inputs
    handleCloseDialog = () => {
        this.setState( { dialogOpen : false , newCategoryName : "" , newMaterial : "" } );
    };

    // Updates the state with the user's input for the new category name.
    // This is used in the "Add Category" dialog to track the input field value.
    handleInputChange = ( event ) => {
        this.setState( { newCategoryName : event.target.value } );
    };

    // Open contextual menu (3-dot icon) for a specific category
    handleMenuOpen = (event, category) => {
        event.preventDefault();
        event.stopPropagation();
        this.setState({
            menuAnchorEl: event.currentTarget,
            selectedCategory: category,
        });
    };

    // Close contextual menu
    handleMenuClose = () => {
        this.setState({
            menuAnchorEl: null,
            selectedCategory: null,
        });
    };

    // Add a new category using Firebase and update local state
    handleAddCategory = async () => {
        console.log(
            "Adding category:" ,
            this.state.newCategoryName ,
            this.state.newMaterial
        );
        const { newCategoryName , newMaterial } = this.state;
        if ( !newCategoryName.trim() ) return;

        try {
            const newCategoryId = await addCategory( newCategoryName , newMaterial );
            const newCategory = {
                id : newCategoryId ,
                name : newCategoryName ,
                questionCount : 0 ,
            };
            this.setState( ( prev ) => ({
                categories : [...prev.categories , newCategory] ,
                dialogOpen : false ,
                newCategoryName : "" ,
                newMaterial : "" ,
            }) );
        } catch ( error ) {
            console.error( "Error adding category:" , error );
        }
    };

    // Open the edit dialog pre-filled with the selected category data
    handleOpenEditDialog = () => {
        const { selectedCategory } = this.state;
        this.setState({
            editDialogOpen: true,
            editCategoryName: selectedCategory.name,
            editCategoryId: selectedCategory.id,
            menuAnchorEl: null,
            selectedCategory: null,
        });
    };

    // Close the edit dialog
    handleCloseEditDialog = () => {
        this.setState( { editDialogOpen : false , editCategoryName : "" , editCategoryId : null } );
    };

    // Save the updated category name
    handleSaveEditCategory = async () => {
        const { editCategoryId , editCategoryName } = this.state;
        if ( !editCategoryName.trim() ) return;

        try {
            await updateCategoryName( editCategoryId , editCategoryName );
            this.setState( ( prev ) => ({
                categories : prev.categories.map( ( c ) =>
                    c.id === editCategoryId ? { ...c , name : editCategoryName } : c
                ) ,
                editDialogOpen : false ,
                editCategoryName : "" ,
                editCategoryId : null ,
            }) );
        } catch ( err ) {
            console.error( "Error updating category:" , err );
        }
    };

    // Delete the selected category after confirmation
    handleDeleteCategory = async () => {
        const { selectedCategory } = this.state;
        if (!window.confirm("האם למחוק את יחידת הלימוד?")) {
            this.handleMenuClose();
            return;
        }

        try {
            await deleteCategory(selectedCategory.id);
            this.setState((prev) => ({
                categories: prev.categories.filter((c) => c.id !== selectedCategory.id),
                menuAnchorEl: null,
                selectedCategory: null,
            }));
        } catch (err) {
            console.error("Error deleting category:", err);
            this.handleMenuClose();
        }
    };

    // Fetch categories and their question counts on initial mount
    async componentDidMount() {
        try {
            const categories = await getCategories();
            // Add question count to each category
            const categoriesWithCounts = await Promise.all(
                categories.map( async ( category ) => {
                    const questionCount = await getQuestionCountByCategory( category.id );
                    return { ...category , questionCount };
                } )
            );
            this.setState( { categories : categoriesWithCounts } );
        } catch ( error ) {
            console.error( "Error fetching categories:" , error );
        }
    }

    // Detect if text is Hebrew for proper RTL alignment
    isHebrew = (text) => /[\u0590-\u05FF]/.test(text);

    render() {
        const { categories , dialogOpen , newCategoryName } = this.state;
        const isNewMaterialHebrew = this.isHebrew( this.state.newMaterial );
        const isNewCategoryHebrew = this.isHebrew( this.state.newCategoryName )

        return (
            <Box
                sx={{
                    mt : 15 ,
                    px : { xs : 2 , sm : 4 } ,
                    overflowX : "hidden" ,  // ✅ prevents horizontal scroll
                    maxWidth : "100vw" ,    // ✅ prevent accidental overflows
                }}
                dir="rtl"
            >
                {/* Page heading */}
                <Typography variant="h4" align="center" gutterBottom>
                    יחידות הלימוד שלך
                </Typography>

                {/* Display grid of category cards */}
                {categories.length > 0 ? (
                    <Grid container spacing={3} sx={{ mt : 2 }} dir="rtl">
                        {categories.map( ( category ) => (
                            <Grid item xs={12} sm={6} md={4} key={category.id}>
                                <Paper
                                    elevation={3}
                                    sx={{
                                        position: "relative",
                                        borderRadius: '16px',
                                        transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                                        overflow: 'hidden',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0 8px 25px 0 rgba(0,0,0,0.15)'
                                        }
                                    }}
                                >
                                    {/* Menu icon for edit/delete */}
                                    <IconButton
                                        onClick={(e) => this.handleMenuOpen(e, category)}
                                        sx={{
                                            position: "absolute",
                                            top: 8,
                                            right: 8,
                                            zIndex: 10,
                                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                            backdropFilter: 'blur(4px)',
                                            '&:hover': {
                                                backgroundColor: 'rgba(255, 255, 255, 1)',
                                                transform: 'scale(1.1)',
                                            },
                                            transition: 'all 0.2s ease-in-out',
                                        }}
                                        size="small"
                                    >
                                        <MoreVertIcon />
                                    </IconButton>
                                    
                                    {/* Link to category details */}
                                    <ListItemButton
                                        component={Link}
                                        to={`/categories/${category.id}`}
                                        sx={{
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            p: 3,
                                            minHeight: 160,
                                            '&:hover': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                            }
                                        }}
                                    >
                                        {/* Icon */}
                                        <ListItemIcon
                                            sx={{
                                                minWidth: "auto",
                                                mb: 1.5,
                                                color: 'primary.main',
                                                alignSelf: 'center'
                                            }}
                                        >
                                            <MenuBookIcon sx={{ fontSize: 48 }} />
                                        </ListItemIcon>

                                        {/* Category Name */}
                                        <Typography
                                            variant="h6"
                                            component="div"
                                            sx={{
                                                fontWeight: 'medium',
                                                textAlign: 'center',
                                                mb: 1,
                                                direction: this.isHebrew(category.name) ? 'rtl' : 'ltr',
                                                lineHeight: 1.3,
                                            }}
                                        >
                                            {category.name}
                                        </Typography>

                                        {/* Question Count Chip */}
                                        <Chip
                                            label={`${category.questionCount} שאלות`}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                borderRadius: '16px',
                                                fontSize: '0.75rem',
                                                fontWeight: 500,
                                            }}
                                        />
                                    </ListItemButton>
                                </Paper>
                            </Grid>
                        ) )}
                        
                        {/* Context menu for a single category */}
                        <Menu
                            anchorEl={this.state.menuAnchorEl}
                            open={Boolean(this.state.menuAnchorEl)}
                            onClose={this.handleMenuClose}
                            PaperProps={{
                                sx: {
                                    borderRadius: '12px',
                                    boxShadow: '0 4px 20px 0 rgba(0,0,0,0.15)',
                                    minWidth: 160,
                                }
                            }}
                        >
                            <MenuItem onClick={this.handleOpenEditDialog}>
                                <EditIcon sx={{ mr: 1, fontSize: 20 }} />
                                עריכה
                            </MenuItem>
                            <MenuItem
                                onClick={this.handleDeleteCategory}
                                sx={{ color: 'error.main' }}
                            >
                                <DeleteIcon sx={{ mr: 1, fontSize: 20 }} />
                                מחיקה
                            </MenuItem>
                        </Menu>
                    </Grid>

                ) : (
                    // Empty state message when no categories exist
                    <Paper
                        elevation={3}
                        sx={{ mt : 4 , py : 6 , px : 2 , textAlign : "center" , borderRadius : '12px' }}
                    >
                        <Typography variant="h6" gutterBottom>
                            אין לך עדיין יחידות לימוד
                        </Typography>
                        <Typography variant="body1" sx={{ mb : 3 }}>
                            לחץ על הכפתור כדי להוסיף את יחידת הלימוד הראשונה שלך
                        </Typography>
                        <Fab
                            color="primary"
                            aria-label="add"
                            onClick={this.handleOpenDialog}
                            sx={{ mt : 2 }} // Add some margin top to the button
                        >
                            <AddIcon/>
                        </Fab>
                    </Paper>
                )}

                {/* Floating Action Button (FAB) to trigger Add dialog */}
                <Box display="flex" justifyContent="center" sx={{ marginTop : 4 }}>
                    <Fab
                        color="primary"
                        aria-label="add"
                        onClick={this.handleOpenDialog}
                        sx={{
                            position : "fixed" ,
                            bottom : 24 ,
                            right : 24 ,
                            zIndex : 1000 ,
                        }}
                    >
                        <AddIcon/>
                    </Fab>
                </Box>

                {/* --- Dialogs Section --- */}
                <CacheProvider value={cacheRtl}>
                    <ThemeProvider theme={theme}>
                        {/* Add Category Dialog */}
                        <Dialog
                            open={dialogOpen}
                            onClose={this.handleCloseDialog}
                            dir="rtl"
                            fullWidth
                            maxWidth="md"
                            PaperProps={{
                                style : {
                                    width : "50%" ,
                                    maxWidth : "none" ,
                                } ,
                            }}
                        >
                            <DialogTitle>הוספת יחידת לימוד חדשה</DialogTitle>

                            <DialogContent>
                                <TextField
                                    autoFocus
                                    margin="dense"
                                    label="שם יחידת לימוד"
                                    fullWidth
                                    variant="standard"
                                    value={this.state.newCategoryName}
                                    onChange={( e ) => this.setState( { newCategoryName : e.target.value } )}
                                    InputProps={{
                                        inputProps : {
                                            style : {
                                                direction : isNewCategoryHebrew ? 'rtl' : 'ltr' ,   // 🔥 Dynamic direction
                                                textAlign : isNewCategoryHebrew ? 'right' : 'left' , // 🔥 Dynamic alignment
                                            }
                                        } ,
                                        style : {
                                            marginBottom : '10px' ,
                                        } ,
                                    }}
                                />
                                <TextField
                                    margin="dense"
                                    label="חומר לימוד"
                                    fullWidth
                                    variant="outlined"
                                    multiline
                                    rows={15}
                                    value={this.state.newMaterial}
                                    onChange={( e ) => this.setState( { newMaterial : e.target.value } )}
                                    InputProps={{
                                        inputProps : {
                                            style : {
                                                direction : isNewMaterialHebrew ? 'rtl' : 'ltr' ,
                                                textAlign : isNewMaterialHebrew ? 'right' : 'left' ,
                                            }
                                        } ,
                                        style : {
                                            maxHeight : '700px' ,
                                            overflow : 'auto' ,
                                        } ,
                                    }}
                                />
                            </DialogContent>

                            <DialogActions style={{ justifyContent : "flex-start" }}>
                                <Button onClick={this.handleCloseDialog}>ביטול</Button>
                                <Button onClick={this.handleAddCategory} variant="contained">
                                    הוסף
                                </Button>
                            </DialogActions>
                        </Dialog>
                                    
                        {/* Edit Category Dialog */}
                        <Dialog
                            open={this.state.editDialogOpen}
                            onClose={this.handleCloseEditDialog}
                            dir="rtl"
                            PaperProps={{
                                style : {
                                    borderRadius : '8px' ,
                                } ,
                            }}
                        >
                            <DialogTitle>עריכת שם יחידת לימוד</DialogTitle>
                            <DialogContent>
                                <TextField
                                    autoFocus
                                    fullWidth
                                    variant="outlined"
                                    margin="dense"
                                    label="שם חדש"
                                    value={this.state.editCategoryName}
                                    onChange={( e ) =>
                                        this.setState( { editCategoryName : e.target.value } )
                                    }
                                />
                            </DialogContent>
                            <DialogActions
                                sx={{ justifyContent : "flex-start" , padding : '16px 24px' }}>
                                <Button onClick={this.handleCloseEditDialog}>ביטול</Button>
                                <Button
                                    onClick={this.handleSaveEditCategory}
                                    variant="contained"
                                    color="primary"
                                >
                                    שמור
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </ThemeProvider>
                </CacheProvider>


            </Box>
        );
    }
}

export default CategoriesView;
